/**
 * `ConversationPageViewer` — what a conversation HEAD opens.
 *
 * Renders `sources/whatsapp/<stem>.conversation.md`, the assembled page
 * `index-builder` writes: the message stream with its media table, inline
 * descriptions and the full-transcript link. Reached only for a row the catalog
 * marks as a compound head (`node_id`) — see `lib/workspace/conversation-page.ts`
 * for why the head is identified by that fact and not by its filename.
 *
 * ## Existence is ATTEMPTED, never inferred
 *
 * The sidecar is a hidden sibling: the runtime keeps companions out of the
 * lawyer's tree (`isInternalFile`), so asking the tree whether the page exists
 * would answer "no" for every conversation ever written. The read is attempted
 * and the outcome decides.
 *
 * ⚠ **`absent` and `unreadable` are NOT the same answer, and that distinction is
 * the whole safety of this component.** A genuine NOT_FOUND means this
 * conversation predates the sidecar (they ship with no backfill), and falling
 * back to today's behaviour is exactly right. A transport failure means we do
 * not KNOW — and silently downgrading the lawyer to the raw export because a
 * read blipped would present the worst representation as though it were the
 * only one, with nothing on screen to say so.
 *
 * ## The fallback is the load-bearing half
 *
 * Every conversation ingested before `mission-ingest`'s roll has no page. Those
 * must open precisely as they did — which for the head's `.txt` is the plain
 * text viewer. Nesting, the back control and the workspace-link classifier all
 * keep working either way; the page's own root-relative links already resolve
 * from the new location because the classifier tries root-relative first.
 */

import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import { conversationPagePathFor } from "@/lib/workspace/conversation-page";
import { displayLabelForPath } from "@/lib/workspace/derive-display-name";
import { basename } from "@/stores/useWorkspaceFileStore";
import { useFileBytes } from "@/hooks/useFileBytes";
import { GatewayRpcError } from "@/lib/gateway-client";
import type { FileRef } from "@/stores/useWorkspaceFileStore";

const LazyMarkdownHtmlViewer = lazy(() =>
  import("./MarkdownHtmlViewer").then((m) => ({ default: m.MarkdownHtmlViewer })),
);

export interface ConversationPageViewerProps {
  file: FileRef;
  onClose?: () => void;
  /** Rendered when the page is genuinely absent — the pre-roll path. The pane
   *  supplies the viewer the file would have got from its extension, so the
   *  fallback is literally "today's behaviour" rather than a second guess at
   *  it.
   *
   *  ⚠ A FUNCTION, not a node: as a node the pane constructed the extension
   *  viewer's element on every render whether or not it was used — including a
   *  lazy `PdfViewer`/`DocxViewer` chunk element built only to be discarded. */
  fallback: () => React.ReactNode;
}

export function ConversationPageViewer({ file, onClose, fallback }: ConversationPageViewerProps) {
  const { t } = useTranslation();
  const pagePath = conversationPagePathFor(file.path);
  // ⚠ READ THE PAGE PATH DIRECTLY. The first cut routed this through
  // `useSourceExtraction`, which does NOT read the path it is handed — it
  // derives its own with `extractionPathForSource`, so a request for
  // `x.conversation.md` silently became `x.conversation.processed.md`, a file
  // that will never exist. The feature would have fallen back to raw text
  // forever, and the live test could not have caught it: on a pre-roll
  // conversation BOTH paths are absent, so the fallback looked identical
  // whether the code was right or wrong. An unfalsifiable negative, in the
  // acceptance test for the very slice whose contract is "attempt the read".
  const bytes = useFileBytes(pagePath);

  // Same discrimination rule the extraction hook uses, matched on the gateway's
  // error CODE and never on its localized prose.
  const notFound =
    bytes.error instanceof GatewayRpcError && bytes.error.code === "NOT_FOUND";
  const state: "loading" | "absent" | "unreadable" | "present" = !pagePath
    ? "absent"
    : bytes.isError
      ? notFound
        ? "absent"
        : "unreadable"
      : bytes.isPending
        ? "loading"
        : bytes.url
          ? "present"
          : "unreadable";

  if (state === "loading") {
    return (
      <div
        data-testid="workspace-file-pane-viewer-conversation"
        data-conversation-state="loading"
        className="flex h-full min-h-0 items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
      >
        <Loader2 size={16} className="animate-spin" />
        {t("workspace.filePane.viewer.loading")}
      </div>
    );
  }

  // ⚠ NOT folded in with `absent`. We could not look; saying nothing and
  // showing the raw export would be a silent downgrade.
  if (state === "unreadable") {
    return (
      <div
        data-testid="workspace-file-pane-viewer-conversation"
        data-conversation-state="unreadable"
        className="flex h-full min-h-0 flex-col items-center justify-center gap-2 p-6 text-center"
      >
        <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
        <p className="max-w-md text-[13px] text-amber-700 dark:text-amber-400">
          {t("workspace.filePane.conversation.unreadable")}
        </p>
      </div>
    );
  }

  // Genuinely no page (pre-roll conversation, no backfill) → today's behaviour.
  if (state === "absent" || !pagePath) {
    return (
      <div
        data-testid="workspace-file-pane-viewer-conversation"
        data-conversation-state="fallback"
        className="flex h-full min-h-0 flex-col"
      >
        {fallback()}
      </div>
    );
  }

  return (
    <div
      data-testid="workspace-file-pane-viewer-conversation"
      data-conversation-state="present"
      data-conversation-page={pagePath}
      className="flex h-full min-h-0 flex-col"
    >
      <Suspense fallback={null}>
        {/* The page is authored by our own pipeline, but it EMBEDS third-party
            content (message text, filenames), so raw HTML stays off — same
            ruling `ExtractedSourceViewer` follows. The title shown is the
            HEAD's, not the sidecar's slug: the lawyer opened the conversation,
            not a companion file. */}
        <LazyMarkdownHtmlViewer
          file={{
            ...file,
            path: pagePath,
            // ⚠ Measured: with no `title` on the ref (the URL-sync path never
            // sets one) the info bar derived its label from the PATH it was
            // given — the sidecar's slug — and the bar read
            // "…20260811-041528.conversation". The docblock two lines up
            // claimed the head's title was shown, so the comment was ahead of
            // the code. Derive from the HEAD's path instead: the lawyer opened
            // a conversation, not a companion file.
            title: file.title ?? displayLabelForPath(file.path, basename(file.path)),
          }}
          onClose={onClose}
          allowRawHtml={false}
          // ⚖ Provenance is the HEAD, not the sidecar. A link followed out of
          // this page must come BACK to the conversation the lawyer opened —
          // not to the companion file it happens to be rendered from, which is
          // not a head, carries no `.info`, and therefore lost both the
          // assembled view and the assessment panel on return.
          originRef={file}
        />
      </Suspense>
    </div>
  );
}
